import { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  FileText, Ticket as TicketIcon, CurrencyEur, UserCircle,
  SignOut, ChartBar, List, X,
} from "@phosphor-icons/react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/portal", label: "Übersicht", icon: ChartBar, end: true, testid: "portal-nav-home" },
  { to: "/portal/tickets", label: "Tickets", icon: TicketIcon, testid: "portal-nav-tickets" },
  { to: "/portal/mietkonto", label: "Mietkonto", icon: CurrencyEur, testid: "portal-nav-ledger" },
  { to: "/portal/dokumente", label: "Dokumente", icon: FileText, testid: "portal-nav-docs" },
  { to: "/portal/profil", label: "Mein Profil", icon: UserCircle, testid: "portal-nav-profile" },
];

export default function PortalLayout() {
  const { user, tenant, logout } = useAuth();
  const navigate = useNavigate();
  const [tenancy, setTenancy] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    api.get("/portal/tenancy").then((r) => setTenancy(r.data)).catch(() => {});
  }, []);

  const brandName = tenant?.display_name || "Domivus";

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      {/* Top header */}
      <header className="bg-white/85 backdrop-blur border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-3">
          <Link to="/portal" className="flex items-center gap-2.5 min-w-0" data-testid="portal-brand">
            {tenant?.logo_url ? (
              <img src={tenant.logo_url} alt={brandName} className="h-9 w-auto max-w-[140px] sm:max-w-[180px] object-contain" />
            ) : (
              <>
                <img src="/logo.svg" alt="Domivus" className="w-8 h-8 shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold tracking-tight leading-tight truncate">{brandName}</div>
                  <div className="text-[10px] text-slate-500 leading-tight uppercase tracking-wider">Mieterportal</div>
                </div>
              </>
            )}
          </Link>
          <div className="flex items-center gap-2">
            {tenancy?.unit && (
              <div className="hidden md:block text-right text-xs">
                <div className="text-slate-500">Ihre Wohnung</div>
                <div className="font-mono text-slate-900">{tenancy.unit.unit_number}</div>
              </div>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 hover:bg-slate-100 px-2 sm:px-3" data-testid="portal-user-menu">
                  <UserCircle size={22} />
                  <span className="text-sm hidden sm:inline truncate max-w-[120px]">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel className="truncate max-w-[240px]">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/login"); }} data-testid="portal-logout">
                  <SignOut size={16} className="mr-2" /> Abmelden
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {/* mobile menu toggle */}
            <button
              type="button"
              className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-md hover:bg-slate-100"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Menü"
              data-testid="portal-menu-toggle"
            >
              {menuOpen ? <X size={22} /> : <List size={22} />}
            </button>
          </div>
        </div>
        {/* Desktop tab nav */}
        <div className="max-w-6xl mx-auto px-6 -mb-px hidden lg:block">
          <div className="flex gap-1 overflow-x-auto">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to} to={n.to} end={n.end}
                  data-testid={n.testid}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 px-3 py-2.5 text-sm transition-colors border-b-2 ${
                      isActive
                        ? "border-slate-900 text-slate-900 font-medium"
                        : "border-transparent text-slate-600 hover:text-slate-900"
                    }`
                  }
                >
                  <Icon size={16} />
                  {n.label}
                </NavLink>
              );
            })}
          </div>
        </div>
        {/* Mobile drawer nav */}
        {menuOpen && (
          <nav className="lg:hidden border-t border-slate-200 bg-white" data-testid="portal-mobile-nav">
            {NAV.map((n) => {
              const Icon = n.icon;
              return (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-5 py-3 text-base border-b border-slate-100 ${
                      isActive ? "bg-slate-900 text-white font-medium" : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  <Icon size={20} />
                  {n.label}
                </NavLink>
              );
            })}
          </nav>
        )}
      </header>

      <main className="flex-1 max-w-6xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8">
        <Outlet context={{ tenancy }} />
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 text-[11px] text-slate-500 flex flex-col sm:flex-row sm:justify-between gap-1">
          <span>© 2026 {brandName} · Mieterportal</span>
          <span>powered by Domivus · v1.0</span>
        </div>
      </footer>
    </div>
  );
}
